'use client';

import { c as _c } from "react/compiler-runtime";
import { useCallback } from 'react';
import { useQueryState, useQueryStates, parseAsArrayOf, parseAsBoolean, parseAsFloat, parseAsIndex, parseAsInteger, parseAsString, parseAsStringLiteral } from 'nuqs';
import { I18N_CONF } from '@funkey/locales/config';
import { ACTIVITY_LOCATION_TYPES, ACTIVITY_CLEANLINESS_TYPES, ACTIVITY_LOCATION_FILTER_TYPES, SEASONS } from '@funkey/constants';
import type { UIMessage } from 'ai';
import { isToolUIPart, getToolName } from 'ai';
import { SearchActivitiesFiltersSchema, SearchActivitiesOutputSchema, RetrieveFaqsOutputSchema, type SearchActivitiesFilters, type SearchActivitiesOutput } from '@funkey/ai-tools/schemas';
type SearchToolResult = {
  state: string;
  activityIds: number[];
  /** Each ranked activity paired with its group (ADR 0007). Falls back to
   * `groupId: null` per id for older messages that predate `activityGroups`. */
  activityGroups: {
    activityId: number;
    groupId: number | null;
  }[];
  scores: NonNullable<SearchActivitiesOutput['scores']>;
  filters: SearchActivitiesFilters | null;
};
type FaqToolResult = {
  state: string;
  activityIds: number[];
  /** The model's intent: should these activities also show in the grid? */
  surfaceInGrid: boolean;
};

/** Dedupe while preserving order (retrieval is similarity-ordered). */
export function dedupeIds(ids: ReadonlyArray<number>): number[] {
  return Array.from(new Set(ids));
}
type SearchAndFaqResult = {
  search: SearchToolResult | null;
  faq: FaqToolResult | null;
};

/** Extracts the last `search_activities`/`retrieve_faqs` tool parts from ONE message. */
function extractSearchAndFaqFromMessage(msg: UIMessage): SearchAndFaqResult {
  let search: SearchToolResult | null = null;
  let faq: FaqToolResult | null = null;
  for (let j = msg.parts.length - 1; j >= 0; j--) {
    const part = msg.parts[j];
    if (!part || !isToolUIPart(part)) continue;
    const toolName = getToolName(part);
    if (toolName === 'search_activities' && !search) {
      const inputParse = 'input' in part ? SearchActivitiesFiltersSchema.safeParse(part.input) : null;
      const outputParse = 'output' in part ? SearchActivitiesOutputSchema.safeParse(part.output) : null;
      const output = outputParse?.success ? outputParse.data : null;
      search = {
        state: part.state,
        activityIds: output?.activityIds ?? [],
        // Prefer the ADR-0007 group pairing; older messages carry only
        // `activityIds`, so treat each as ungrouped (resolved directly).
        activityGroups: output?.activityGroups ?? (output?.activityIds ?? []).map(id => ({
          activityId: id,
          groupId: null
        })),
        scores: output?.scores ?? [],
        filters: output?.filters ?? (inputParse?.success ? inputParse.data : null)
      };
    } else if (toolName === 'retrieve_faqs' && !faq) {
      const outputParse = 'output' in part ? RetrieveFaqsOutputSchema.safeParse(part.output) : null;
      const output = outputParse?.success ? outputParse.data : null;
      faq = {
        state: part.state,
        activityIds: dedupeIds((output?.faqs ?? []).map(f => f.activityId)),
        surfaceInGrid: output?.surfaceInGrid ?? false
      };
    }
  }
  return {
    search,
    faq
  };
}

/**
 * Gather the `search_activities` and/or `retrieve_faqs` results from the latest
 * message that holds either — so the decision step can blend them per the model's
 * `surfaceInGrid` intent.
 *
 * @internal — exported for unit tests only.
 */
export function pickLatestSearchToolPart(messages: ReadonlyArray<UIMessage>): SearchAndFaqResult | null {
  for (let i = messages.length - 1; i >= 0; i--) {
    const msg = messages[i];
    if (!msg) continue;
    const result = extractSearchAndFaqFromMessage(msg);
    if (result.search || result.faq) return result;
  }
  return null;
}

/**
 * Same extraction as `pickLatestSearchToolPart`, but pinned to ONE specific
 * message id rather than "whichever is latest" — backs "go back" navigation
 * to a past search turn (`useSelectedSearchTurn`'s override).
 *
 * @internal — exported for unit tests only.
 */
export function pickSearchToolPartForMessage(messages: ReadonlyArray<UIMessage>, messageId: string): SearchAndFaqResult | null {
  const msg = messages.find(m => m.id === messageId);
  if (!msg) return null;
  const result = extractSearchAndFaqFromMessage(msg);
  return result.search || result.faq ? result : null;
}
type BridgeUrlPayload = {
  query: string | null;
  minBudget: number | null;
  maxBudget: number | null;
  participants: number | null;
  // Structured hard-filters the AI applied. Written to the URL so the chips + grid
  // reflect them and STALE values (e.g. a prior drawer selection) are replaced — a
  // turn that doesn't set them writes `null` to clear. Empty array → null.
  locationType: ('INDOOR' | 'OUTDOOR' | 'VIRTUAL')[] | null;
  season: ('WINTER' | 'SPRING' | 'SUMMER' | 'FALL')[] | null;
  cleanliness: ('DIRTY' | 'WET' | 'CLEAN')[] | null;
  // Resolved catalogue category ids (the AI's category NAMES resolved server-side to
  // the SAME ids pgvector matched). Written so the chips + drawer show the category and
  // the ES/filters grid actually narrows by it — a turn without categories writes `null`
  // to clear a stale selection. ROOT ids and SUBcategory ids go to separate URL params
  // because the drawer toggles + ES fields keep them on different axes.
  categories: number[] | null;
  subCategories: number[] | null;
  // Team-dynamic traits the AI applied. The AI emits presence ('action' = "wants
  // action"); the grid/drawer/ES model each as selected importance levels, so a
  // requested trait maps to [2,3] — the SAME `>= 2` cut the vector path applies —
  // and both modes filter identically. A turn that doesn't set a trait writes `null`.
  teamwork: number[] | null;
  action: number[] | null;
  competition: number[] | null;
  learning: number[] | null;
  // AI `duration` bucket converted to the grid's minute bounds (the URL models
  // duration as minDuration/maxDuration, not a bucket). Without this the duration
  // filter was lost in filters mode and the grid returned every duration.
  minDuration: number | null;
  maxDuration: number | null;
  // Coordinate distance search: written together so the grid shows the distance
  // chip + pills and sorts nearest-first. A turn without coords writes `null` to
  // clear any stale city-search params.
  nearLat: number | null;
  nearLng: number | null;
  nearLabel: string | null;
  radius: number | null;
  location: ('FIXED' | 'CUSTOM')[] | null;
  sort: 'distance.ASC' | 'price.ASC' | 'price.DESC' | 'popularity.DESC' | null;
  page: number;
  // Pins `useSelectedSearchTurn` to a specific past turn ("go back" navigation).
  // `null` = default (the latest turn with results) — written on every new turn.
  resultMessageId: string | null;
};

/**
 * Pure mapper from a `search_activities` result (filters + the pgvector id set)
 * to the URL overlay the grid reads. Shared by the "re-apply a past result"
 * click handler (`useApplyAiResult`) and the AI → Filter toggle-time mapper
 * (`mapAiFiltersToFilterState`, `activity-ai-mode-carryover.ts`, FUN-4891).
 *
 * @internal — exported for unit tests.
 */
export function buildBridgeWrite(filters: SearchActivitiesFilters, activityIds: number[], resultMessageId: string | null = null): {
  urlPayload: BridgeUrlPayload;
  activityIds: number[];
} {
  const hasAiResults = activityIds.length > 0;
  // AI traits are presence-only ('action' = "wants action"); the grid/drawer/ES model
  // each trait as a set of selected importance levels. Map a requested trait to [2,3]
  // ("important"/"very important") — the SAME `>= 2` cut the vector path applies — so
  // filters-mode ES + the drawer + chip match the semantic path. `null` clears it.
  const traitLevels = (trait: 'teamwork' | 'action' | 'competition' | 'learning') => filters.traits?.includes(trait) ? [2, 3] : null;
  return {
    urlPayload: {
      // In AI mode the activityIDs (stored in sessionStorage) *are* the
      // result set. pgvector already applied the hard filters during
      // retrieval, so we clear the URL-side overlay filters when AI
      // returned results; otherwise fall back to the legacy filter URL.
      // `|| null`, not `?? null`: a filter-only turn echoes `query: ''`
      // (no keyword) and an empty string must clear the URL keyword rather
      // than search for "" — otherwise the pure-filter grid
      // would diverge from the non-AI filter mode. The `query` keyword stays
      // cleared in AI mode (the id set drives the grid, not a keyword).
      query: hasAiResults ? null : filters.query || null,
      // Budget IS written even with AI results, so the chip + drawer reflect the
      // budget the AI applied (and it matches the summary). `getByIds` ignores it
      // (display-only); the ES/filter paths apply it correctly.
      minBudget: filters.minBudget ?? null,
      maxBudget: filters.maxBudget ?? null,
      // Keep `participants` even in AI mode — cards use it to compute the
      // displayed price label. The search ignores it in AI mode.
      participants: filters.participants ?? null,
      // Structured filters the AI applied: write them (replacing stale URL
      // values) so the chips show what was actually applied. In getByIds mode
      // the grid ignores these (pgvector already filtered), so they're
      // display-only there and become active once the id set is cleared.
      locationType: filters.locationType?.length ? filters.locationType : null,
      season: filters.season?.length ? filters.season : null,
      cleanliness: filters.cleanliness?.length ? filters.cleanliness : null,
      // Resolved on the server (`categoryIds`/`subCategoryIds`), NOT the raw
      // names — the URL params hold ids, split root vs sub. In getByIds (vector)
      // mode this is display-only (pgvector already filtered); in filters mode the
      // grid's ES search applies it. `null` clears a stale category this turn unset.
      categories: filters.categoryIds?.length ? filters.categoryIds : null,
      subCategories: filters.subCategoryIds?.length ? filters.subCategoryIds : null,
      teamwork: traitLevels('teamwork'),
      action: traitLevels('action'),
      competition: traitLevels('competition'),
      learning: traitLevels('learning'),
      // Convert the AI duration bucket to the grid's minute bounds. Mirrors the
      // API's DURATION_BUCKET_MINUTES (short ≤120, halfDay 120–240, fullDay >240)
      // so the filters-mode grid applies the same cut as the semantic path.
      minDuration: filters.duration === 'halfDay' ? 120 : filters.duration === 'fullDay' ? 240 : null,
      maxDuration: filters.duration === 'short' ? 120 : filters.duration === 'halfDay' ? 240 : null,
      // Coordinate distance search: when the AI geocoded a place
      // (`nearLat`/`nearLng` both present), write the city-search params so the
      // grid shows distance pills + sorts nearest-first. Otherwise write `null`
      // to clear any stale city-search from a previous turn.
      ...(filters.nearLat != null && filters.nearLng != null ? {
        nearLat: filters.nearLat,
        nearLng: filters.nearLng,
        nearLabel: filters.nearLabel ?? null,
        radius: filters.radius ?? null,
        nearCleared: null,
        location: filters.route?.length ? filters.route : null,
        // An explicit ordering intent (e.g. "cheapest near Gent")
        // wins over the distance default a place search applies.
        sort: filters.sort ?? 'distance.ASC'
      } : {
        nearLat: null,
        nearLng: null,
        nearLabel: null,
        radius: null,
        nearCleared: null,
        location: null,
        sort: filters.sort ?? null
      }),
      page: 0,
      resultMessageId
    },
    activityIds
  };
}
const BRIDGE_URL_PARSERS = {
  query: parseAsString,
  minBudget: parseAsFloat,
  maxBudget: parseAsFloat,
  participants: parseAsInteger,
  countryCode: parseAsStringLiteral(I18N_CONF.COUNTRIES),
  categories: parseAsArrayOf(parseAsInteger),
  subCategories: parseAsArrayOf(parseAsInteger),
  teamwork: parseAsArrayOf(parseAsInteger),
  action: parseAsArrayOf(parseAsInteger),
  competition: parseAsArrayOf(parseAsInteger),
  learning: parseAsArrayOf(parseAsInteger),
  locationType: parseAsArrayOf(parseAsStringLiteral(ACTIVITY_LOCATION_TYPES)),
  season: parseAsArrayOf(parseAsStringLiteral(SEASONS)),
  cleanliness: parseAsArrayOf(parseAsStringLiteral(ACTIVITY_CLEANLINESS_TYPES)),
  minDuration: parseAsInteger,
  maxDuration: parseAsInteger,
  // Coordinate distance search params — SAME parsers as the grid's filter bar
  // (`ACTIVITY_FILTER_BAR_QUERY_PARAMS` + `ACTIVITY_SORT_QUERY_PARAMS`).
  nearLat: parseAsFloat,
  nearLng: parseAsFloat,
  nearLabel: parseAsString,
  radius: parseAsInteger,
  nearCleared: parseAsBoolean,
  location: parseAsArrayOf(parseAsStringLiteral(ACTIVITY_LOCATION_FILTER_TYPES)),
  sort: parseAsStringLiteral(['popularity.DESC', 'price.ASC', 'price.DESC', 'createdAt.ASC', 'createdAt.DESC', 'distance.ASC']),
  page: parseAsIndex,
  resultMessageId: parseAsString
} as const;

/**
 * The `resultMessageId` URL param that pins `useSelectedSearchTurn` to a past
 * turn (`ActivityFilterDrawer`'s "Change search" writes `DISMISSED_SEARCH_TURN`
 * here; the grid reads it back). One shared hook so the key/parser can't drift
 * between the read side (the grid) and the write side (the drawer).
 */
export function useResultMessageIdParam() {
  return useQueryState("resultMessageId", parseAsString);
}

/**
 * Returns an `applyResult` callback that pushes a specific `search_activities`
 * result's URL overlay onto the grid, triggered by clicking a past chat result
 * to "go back" to it. `resultMessageId` pins `useSelectedSearchTurn` to that
 * turn (`null` = the default "latest turn with results"). The grid re-derives
 * the actual id set from `chat.messages`, so there's nothing else to write
 * here. A manual apply survives until the next turn is sent.
 */
export function useApplyAiResult() {
  const $ = _c(3);
  let t0;
  if ($[0] === Symbol.for("react.memo_cache_sentinel")) {
    t0 = {
      shallow: true,
      scroll: false,
      clearOnDefault: true,
      throttleMs: 200
    };
    $[0] = t0;
  } else {
    t0 = $[0];
  }
  const [, setUrlState] = useQueryStates(BRIDGE_URL_PARSERS, t0);
  let t1;
  if ($[1] !== setUrlState) {
    t1 = (filters, activityIds, t2) => {
      const resultMessageId = t2 === undefined ? null : t2;
      const write = buildBridgeWrite(filters, activityIds, resultMessageId);
      setUrlState(write.urlPayload);
    };
    $[1] = setUrlState;
    $[2] = t1;
  } else {
    t1 = $[2];
  }
  return t1;
}